import { useCallback, useMemo, useRef } from 'react';
import { debounce } from 'utils/helper';
import { useSwimlaneActions } from 'hooks/useApiActions';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';

const getNextCategoryPosition = items => {
  const positions = (items || [])
    .map(item => Number(item?.position))
    .filter(Number.isFinite);

  if (positions.length) {
    return Math.max(...positions) + 1;
  }

  return items?.length || 0;
};

const clampInsertIndex = (index, length) => {
  const numericIndex = Number(index);
  if (!Number.isFinite(numericIndex)) return length;
  return Math.min(Math.max(Math.trunc(numericIndex), 0), length);
};

const withSequentialPositions = items =>
  items.map((item, position) => ({
    ...item,
    position,
  }));

/**
 * Custom hook for managing swimlane item CRUD operations
 * Centralizes all item-related actions and state management
 */
export const useSwimlaneItemActions = ({
  type,
  budgetId,
  selectedItem,
  swimlane,
  swimlaneItems,
  setSwimLaneItems,
  refetchBudgetId,
  setShowDetailedView,
  setEditing,
  swimlanes,
  refetchSwimlaneCosts,
  itemKeyRef,
  onSwimlaneDeleted,
}) => {
  const { createSwimlane, updateSwimlane, deleteSwimlane } =
    useSwimlaneActions();

  const {
    addSubCategoryMutation,
    createDuplicateCategory,
    createDuplicateCategoryLineItem,
  } = useHomeScreenContext();

  const newItemCache = useRef({});

  const handleAddItem = useCallback(
    async (name, addMore, options = {}) => {
      if (!name) return;

      const newItemTempID = `NEW_ITEM_${new Date().valueOf()}`;
      const nextPosition = getNextCategoryPosition(swimlaneItems);
      const requestedPosition = Number.isFinite(Number(options.position))
        ? Number(options.position)
        : nextPosition;
      const optimisticPosition = Number.isFinite(
        Number(options.optimisticPosition)
      )
        ? Number(options.optimisticPosition)
        : requestedPosition;
      const newItem = {
        name,
        id: newItemTempID,
        cost: 0,
        position: optimisticPosition,
        ...(options.section !== undefined ? { section: options.section } : {}),
      };
      const insertIndex = clampInsertIndex(
        options.insertIndex,
        swimlaneItems.length
      );

      // Optimistic update
      const optimisticItems = [...swimlaneItems];
      optimisticItems.splice(insertIndex, 0, newItem);
      setSwimLaneItems(
        options.normalizePositions
          ? withSequentialPositions(optimisticItems)
          : optimisticItems
      );

      const payload = {
        type,
        budgetId,
        selectedItem,
        name,
        swimlane,
        section: options.section || null,
        position: requestedPosition,
      };

      try {
        const response = await createSwimlane(payload).unwrap();
        const persistedItem = {
          ...newItem,
          ...response,
          position: Number(response?.position ?? requestedPosition),
        };

        setSwimLaneItems(currentItems => {
          const itemsWithPersistedCategory = currentItems.map(item =>
            item?.id === newItemTempID ? persistedItem : item
          );

          return options.normalizePositions
            ? withSequentialPositions(itemsWithPersistedCategory)
            : itemsWithPersistedCategory;
        });

        newItemCache.current = {
          ...newItemCache.current,
          [newItemTempID]: response,
        };
        const tempKey = itemKeyRef.current.uniqueKeys[newItemTempID];
        if (tempKey && response?.id) {
          itemKeyRef.current.uniqueKeys[response.id] = tempKey;
          delete itemKeyRef.current.uniqueKeys[newItemTempID];
        }
        return response;
      } catch (error) {
        // Revert optimistic update on error
        setSwimLaneItems(swimlaneItems);
        throw error;
      }
    },
    [
      type,
      budgetId,
      selectedItem,
      swimlane,
      swimlaneItems,
      setSwimLaneItems,
      createSwimlane,
      itemKeyRef,
    ]
  );

  const handleItemUpdate = useMemo(
    () =>
      debounce(async (itemId, field, value, options = {}) => {
        const item = swimlaneItems.find(i => i.id === itemId);
        if (!item) return;

        // Optimistic update
        const updatedItems = swimlaneItems.map(i =>
          i.id === itemId ? { ...i, [field]: value } : i
        );
        setSwimLaneItems(updatedItems);

        const payload = {
          type,
          id: itemId,
          [field]: value,
          ...(options.suppressSuccessToast
            ? { suppressSuccessToast: true }
            : {}),
        };

        try {
          await updateSwimlane(payload).unwrap();
          refetchBudgetId?.();
        } catch (error) {
          // Revert optimistic update on error
          setSwimLaneItems(swimlaneItems);
          throw error;
        }
      }, 500),
    [type, swimlaneItems, setSwimLaneItems, updateSwimlane, refetchBudgetId]
  );

  const handleItemNameChange = useCallback(
    item => e => {
      const name = e?.target.value;
      if (name === '') {
        // Category name cannot be empty
        return;
      }

      if (item?.name !== name) {
        const payload = { ...item, name };
        // Update local state immediately
        const updatedItems = swimlaneItems.map(swimlaneItem =>
          swimlaneItem?.id === item?.id ? payload : swimlaneItem
        );
        setSwimLaneItems(updatedItems);

        // Update via Redux action
        handleItemUpdate(item.id, 'name', name, {
          suppressSuccessToast: true,
        });
        setEditing({ [item?.id]: false });

        // Update detailed view if needed
        setShowDetailedView(prev => ({ ...prev, selectedItem: payload }));
      } else {
        setEditing({ [item?.id]: false });
      }
    },
    [
      swimlaneItems,
      setSwimLaneItems,
      handleItemUpdate,
      setEditing,
      setShowDetailedView,
    ]
  );

  const handleDeleteItem = useCallback(
    async itemId => {
      const payload = {
        type,
        id: itemId,
      };

      try {
        await deleteSwimlane(payload).unwrap();
        refetchSwimlaneCosts?.();
        // Clear any currently selected swimlane / details view
        setShowDetailedView?.(false);
        // If this is a top-level swimlane category, also clear swimlane nav info
        if (type === 'swimlane') {
          onSwimlaneDeleted?.();
        }
        return true;
      } catch (error) {
        console.error('Error deleting swimlane item:', error);
        return false;
      }
    },
    [
      type,
      deleteSwimlane,
      refetchSwimlaneCosts,
      setShowDetailedView,
      onSwimlaneDeleted,
    ]
  );

  const handleCreateSubCategory = useCallback(
    item => {
      const payload = {
        id: item?.id,
        type,
      };

      addSubCategoryMutation.mutate(payload, {
        onSuccess: async () => {
          await refetchBudgetId?.();
          return item; // Return item for further processing
        },
      });
    },
    [type, addSubCategoryMutation, refetchBudgetId]
  );

  const handleDuplicateCategory = useCallback(
    item => {
      if (type === 'swimlane') {
        const payload = {
          swimlane_id: item?.id,
        };
        createDuplicateCategory.mutate(payload, {
          onSuccess: () => {
            refetchSwimlaneCosts?.();
          },
        });
      } else {
        const payload = {
          line_item_id: item?.id,
        };
        createDuplicateCategoryLineItem.mutate(payload, {
          onSuccess: () => {
            refetchSwimlaneCosts?.();
          },
        });
      }
    },
    [
      type,
      createDuplicateCategory,
      createDuplicateCategoryLineItem,
      refetchSwimlaneCosts,
    ]
  );

  const handleDropdownAction = useCallback(
    async (action, item) => {
      switch (action) {
        case 'duplicate-item':
          handleDuplicateCategory(item);
          break;
        case 'add-sub-category':
          handleCreateSubCategory(item);
          break;
        case 'delete-item':
          await handleDeleteItem(item.id);
          break;
        default:
          break;
      }
    },
    [handleDuplicateCategory, handleCreateSubCategory, handleDeleteItem]
  );

  return {
    handleAddItem,
    handleItemUpdate,
    handleItemNameChange,
    handleDeleteItem,
    handleCreateSubCategory,
    handleDuplicateCategory,
    handleDropdownAction,
    newItemCache,
  };
};
